import dayjs from "dayjs"
import { mbGbDisplay } from "./helpers"
import { PieDataModel, TerminalPieStats, UsageMetrics } from "./models"
import utc from "dayjs/plugin/utc"
dayjs.extend(utc)

export const getDateWithUTC = (date: any) => dayjs(date)

export const RESOLUTIONS = {
  year: "1M",
  month: "1d",
  day: "1h",
  allTime: "1Y",
} as const

export type Resolution = (typeof RESOLUTIONS)[keyof typeof RESOLUTIONS]

export const getTimeTitle = (date: any, format: Resolution) => {
  if (format === RESOLUTIONS.year) {
    return getDateWithUTC(date).year()
  } else if (format === RESOLUTIONS.month) {
    return getDateWithUTC(date).format("MMM YYYY")
  } else if (format === RESOLUTIONS.day) {
    return getDateWithUTC(date).format("DD MMM YYYY")
  } else {
    return "All time"
  }
}

export const getFromAndToTime = (prevFrom: any, format: Resolution, clickedBack: boolean) => {
  if (clickedBack) {
    if (format === RESOLUTIONS.year) {
      //Back one year
      const prevDate = getDateWithUTC(prevFrom)
      const fromDate = prevDate.subtract(1, "year").format("YYYY-MM-DDTHH:mm:ssZ")
      const toDate = getDateWithUTC(prevFrom).format("YYYY-MM-DDTHH:mm:ssZ")

      return { from: fromDate, to: toDate }
    } else if (format === RESOLUTIONS.month) {
      //Go back one month
      const fromDate = getDateWithUTC(prevFrom).subtract(1, "day").startOf("month").format("YYYY-MM-DDTHH:mm:ssZ")
      const toDate = getDateWithUTC(prevFrom).format("YYYY-MM-DDTHH:mm:ssZ")

      return { from: fromDate, to: toDate }
    } else if (format === RESOLUTIONS.day) {
      //Go back one day
      const fromDate = getDateWithUTC(prevFrom).subtract(1, "day").format("YYYY-MM-DDTHH:mm:ssZ")
      const toDate = getDateWithUTC(prevFrom).format("YYYY-MM-DDTHH:mm:ssZ")

      return { from: fromDate, to: toDate }
    }
  } else {
    if (format === RESOLUTIONS.year) {
      //Forward one year
      const prevDate = getDateWithUTC(prevFrom)
      const newFrom = prevDate.add(1, "year").format("YYYY-MM-DDTHH:mm:ssZ")
      const newTo = prevDate.add(2, "year").format("YYYY-MM-DDTHH:mm:ssZ")
      return { from: newFrom, to: newTo }
    } else if (format === RESOLUTIONS.month) {
      //Forward one month
      const prevDate = getDateWithUTC(prevFrom)
      const newFrom = prevDate.add(1, "month").format("YYYY-MM-DDTHH:mm:ssZ")
      const newTo = prevDate.add(2, "month").format("YYYY-MM-DDTHH:mm:ssZ")
      return { from: newFrom, to: newTo }
    } else if (format === RESOLUTIONS.day) {
      //Forward one day
      const prevDate = getDateWithUTC(prevFrom)
      const newFrom = prevDate.add(1, "day").format("YYYY-MM-DDTHH:mm:ssZ")
      const newTo = prevDate.add(2, "day").format("YYYY-MM-DDTHH:mm:ssZ")
      return { from: newFrom, to: newTo }
    }
  }
}

export const getDatesOnChangedResolution = (
  initialFromDate: any,
  newResolution: Resolution,
  clickedSpan: boolean,
  start?: any,
  end?: any,
) => {
  console.log("end date: ", end)

  const today = dayjs()
  if (!clickedSpan) {
    //Change time interval to current time intervals
    if (newResolution === RESOLUTIONS.year) {
      const { fromTimestamp, toTimestamp } = getInitialTimeStamps()
      return { from: fromTimestamp, to: toTimestamp }
    } else if (newResolution === RESOLUTIONS.month) {
      const from = today.startOf("month").startOf("day")
      const to = from.add(1, "month")
      return {
        from: from.format("YYYY-MM-DDTHH:mm:ssZ"),
        to: to.format("YYYY-MM-DDTHH:mm:ssZ"),
      }
    } else if (newResolution === RESOLUTIONS.day) {
      const from = today.startOf("day")
      const to = from.add(1, "day")
      return {
        from: from.format("YYYY-MM-DDTHH:mm:ssZ"),
        to: to.format("YYYY-MM-DDTHH:mm:ssZ"),
      }
    } else {
      return {
        from: getDateWithUTC(start).format("YYYY-MM-DDTHH:mm:ssZ"),
        to: getDateWithUTC(end).format("YYYY-MM-DDTHH:mm:ssZ"),
      }
    }
  } else {
    //User is not looking at todays dates
    if (newResolution === RESOLUTIONS.year) {
      const from = getDateWithUTC(initialFromDate).startOf("year").startOf("month").startOf("day")
      const to = from.add(1, "year")
      return {
        from: from.format("YYYY-MM-DDTHH:mm:ssZ"),
        to: to.format("YYYY-MM-DDTHH:mm:ssZ"),
      }
    } else if (newResolution === RESOLUTIONS.month) {
      const from = getDateWithUTC(initialFromDate).startOf("month").startOf("day")
      const to = from.add(1, "month")
      return {
        from: from.format("YYYY-MM-DDTHH:mm:ssZ"),
        to: to.format("YYYY-MM-DDTHH:mm:ssZ"),
      }
    } else if (newResolution === RESOLUTIONS.day) {
      const from = getDateWithUTC(initialFromDate).startOf("day")
      const to = from.add(1, "day")
      return {
        from: from.format("YYYY-MM-DDTHH:mm:ssZ"),
        to: to.format("YYYY-MM-DDTHH:mm:ssZ"),
      }
    } else {
      return {
        from: getDateWithUTC(start).format("YYYY-MM-DDTHH:mm:ssZ"),
        to: getDateWithUTC(end).format("YYYY-MM-DDTHH:mm:ssZ"),
      }
    }
  }
}

export const getFromAndToTimestampsFromColumnClick = (fromTimestamp: any, currentResolution: Resolution) => {
  const newFromDate = getDateWithUTC(fromTimestamp)
  if (currentResolution === RESOLUTIONS.year) {
    const newToDate = newFromDate.add(1, "month")
    return {
      from: newFromDate.format("YYYY-MM-DDTHH:mm:ssZ"),
      to: newToDate.format("YYYY-MM-DDTHH:mm:ssZ"),
    }
  } else if (currentResolution === RESOLUTIONS.month) {
    const newToDate = newFromDate.add(1, "day")
    return {
      from: newFromDate.format("YYYY-MM-DDTHH:mm:ssZ"),
      to: newToDate.format("YYYY-MM-DDTHH:mm:ssZ"),
    }
  } else if (currentResolution === RESOLUTIONS.allTime) {
    const newToDate = newFromDate.add(1, "year")
    return {
      from: newFromDate.format("YYYY-MM-DDTHH:mm:ssZ"),
      to: newToDate.format("YYYY-MM-DDTHH:mm:ssZ"),
    }
  } else {
    return { from: "error", to: "error" }
  }
}

const accumulatedPies = (pieData: Array<PieDataModel>) => {
  let collectedPercentage = 0
  pieData.map((pie) => (collectedPercentage = collectedPercentage + pie.y))

  const newPieObj = { x: "other", y: collectedPercentage, label: "Other" }
  return newPieObj
}

export const slicePieData = (pieData: Array<PieDataModel>) => {
  const sortedPieData = pieData?.slice().sort((a, b) => b.y - a.y)

  const firstPieBits = sortedPieData.slice(0, 9)
  const lastPieBits = sortedPieData.slice(10)

  const lastPie = lastPieBits.length > 0 ? accumulatedPies(lastPieBits) : undefined

  const pies = lastPie ? [...firstPieBits, lastPie] : sortedPieData

  return pies
}

export const apiPieDataToPieModel = (pieData: Array<TerminalPieStats>) => {
  const pies: Array<PieDataModel> = []

  pieData.map((pie: TerminalPieStats) => {
    const obj: PieDataModel = {
      x: pie.terminalSerialNumber,
      y: pie.percentage,
      label: `${pie.terminalNickname} \n ${mbGbDisplay(pie.totalUsageAggregated)} \n ${Math.round(
        pie.percentage * 100,
      )}%`,
    }
    pie.percentage !== 0 && pies.push(obj)
  })

  return pies
}

const yd = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
export interface ColumnData {
  time: number
  dataUsage: number
  label: string
  fromTimestamp?: any
}

export const usageDataToColumnDataYear = (usageData: Array<UsageMetrics>) => {
  const data: Array<ColumnData> = []

  yd.map((month, index) => {
    const time = index + 1
    const monthData = usageData.find((y) => getDateWithUTC(y.timeStamp).month() === month)
    const dataUsage = monthData?.mbCount ?? 0
    const label = `${mbGbDisplay(monthData?.mbCount ?? 0)}`

    data.push({ time: time, dataUsage: dataUsage, label: label })
  })

  return data
}

export const usageDataToColumnDataYearShared = (usageData: Array<UsageMetrics>) => {
  const data: Array<ColumnData> = []

  //console.log("Usage data: ", usageData);

  yd.map((month, index) => {
    const time = index + 1
    const dataPointOfCurrentMonth = usageData.filter((y) => getDateWithUTC(y.timeStamp).month() === month)
    const totalDataUsageOfMonth = dataPointOfCurrentMonth.reduce((acc, obj) => {
      return acc + (obj.mbCount ?? 0)
    }, 0)

    const timestamp = dataPointOfCurrentMonth[0]?.timeStamp

    const label = `${mbGbDisplay(totalDataUsageOfMonth)} \n ${getDateWithUTC(timestamp).format("MMM YYYY")}`

    const fromTimeStamp = getDateWithUTC(timestamp).startOf("month")

    data.push({
      time: time,
      dataUsage: totalDataUsageOfMonth,
      label: label,
      fromTimestamp: fromTimeStamp,
    })
  })

  return data
}

export const usageDataToColumnDataMonth = (usageData: Array<UsageMetrics>, monthDate: any) => {
  const daysInMonth = getDateWithUTC(monthDate).daysInMonth()
  const data = []
  //console.log("Usage data: ", usageData);

  for (let i = 0; i <= daysInMonth; i++) {
    const time = i + 1
    const dayData = usageData.find((y) => getDateWithUTC(y.timeStamp).date() === i + 1)
    const dataUsage = dayData?.mbCount ?? 0
    const label = `${mbGbDisplay(dayData?.mbCount ?? 0)} \n ${getDateWithUTC(dayData?.timeStamp).format("DD MMM")}`

    const fromTimtestamp = getDateWithUTC(dayData?.timeStamp).startOf("day")

    data.push({
      time: time,
      dataUsage: dataUsage,
      label: label,
      fromTimestamp: fromTimtestamp,
    })
  }

  return data
}

export const usageDataToColumnDataMonthShared = (usageData: Array<UsageMetrics>, monthDate: any) => {
  const daysInMonth = getDateWithUTC(monthDate).daysInMonth() //might get wrong date?
  const data = []

  const hasMonth = usageData && usageData[0]
  const month = hasMonth && getDateWithUTC(hasMonth.timeStamp).format("MMM")

  for (let i = 0; i < daysInMonth; i++) {
    const time = i
    const dayData = usageData.filter((y) => getDateWithUTC(y.timeStamp).date() === i + 1)
    const totalDataUsageOfMonth = dayData.reduce((acc, obj) => {
      return acc + (obj.mbCount ?? 0)
    }, 0)
    const label = `${mbGbDisplay(totalDataUsageOfMonth)} \n ${month ?? ""} ${i}`

    const fromTimestamp = getDateWithUTC(dayData[0]?.timeStamp).startOf("day")

    data.push({
      time: time,
      dataUsage: totalDataUsageOfMonth,
      label: label,
      fromTimestamp: fromTimestamp,
    })
  }

  return data
}

export const usageDataToColumnDataDay = (usageData: Array<UsageMetrics>) => {
  const hourseInDay = 24
  const data = []

  for (let i = 0; i < hourseInDay; i++) {
    const time = i
    const hourData = usageData.find((y) => getDateWithUTC(y.timeStamp).hour() == i)
    const dataUsage = hourData?.mbCount ?? 0
    const label = `${mbGbDisplay(hourData?.mbCount ?? 0)} \n ${getDateWithUTC(hourData?.timeStamp).format(
      "DD MMM YYYY",
    )} \n 🕝 ${getDateWithUTC(hourData?.timeStamp).format("HH:mm")} `

    data.push({ time: time, dataUsage: dataUsage, label: label })
  }

  return data
}

export const usageDataToColumnDataDayShared = (usageData: Array<UsageMetrics>) => {
  const hoursInDay = 24
  const data = []

  for (let i = 0; i < hoursInDay; i++) {
    const time = i
    const hourData = usageData.filter((y) => getDateWithUTC(y.timeStamp).hour() == i)
    const dataUsageTotal = hourData.reduce((acc, obj) => {
      return acc + (obj.mbCount ?? 0)
    }, 0)
    const label = `${mbGbDisplay(dataUsageTotal)} \n 🕝 ${time}:00 `

    data.push({ time: time, dataUsage: dataUsageTotal, label: label })
  }

  return data
}

export const getAllTimeColumnLabels = (fromDate: any, toDate: any) => {
  const from = getDateWithUTC(fromDate)
  const to = getDateWithUTC(toDate)
  const fromYear = from.year()
  const toYear = to.year()
  const labels: Array<string> = []

  for (let i = fromYear; i < toYear + 1; i++) {
    labels.push(i.toString())
  }

  return labels
}

export const usageDataToColumnAllTime = (usageData: Array<UsageMetrics>, fromDate: any, toDate: any) => {
  const data: Array<ColumnData> = []
  const from = getDateWithUTC(fromDate)
  const to = getDateWithUTC(toDate)

  const fromYear = from.year()
  const toYear = to.year()

  for (let i = fromYear; i < toYear + 1; i++) {
    const time = toYear - i + 2
    const year = i
    const yd = usageData.find((y) => getDateWithUTC(y.timeStamp).year() === year)
    const dataUsage = yd?.mbCount ?? 0
    const label = yd
      ? `${mbGbDisplay(dataUsage)} \n ${getDateWithUTC(yd?.timeStamp).format("YYYY")}`
      : `0 MB \n ${time}`

    data.push({ time: time, dataUsage: dataUsage, label: label })
  }

  return data
}

export const usageDataToColumnAllTimeShared = (usageData: Array<UsageMetrics>, fromDate: any, toDate: any) => {
  const data: Array<ColumnData> = []
  const from = getDateWithUTC(fromDate)
  const to = getDateWithUTC(toDate)

  const fromYear = from.year()
  const toYear = to.year()

  for (let i = fromYear; i < toYear + 1; i++) {
    const time = fromYear - i + 1
    const year = i
    const yd = usageData.filter((y) => getDateWithUTC(y.timeStamp).year() === year)
    const dataUsage = yd.reduce((acc, obj) => {
      return acc + (obj.mbCount ?? 0)
    }, 0)
    const label = yd ? `${mbGbDisplay(dataUsage)} \n ${year}` : `0 MB \n ${time}`

    const fromTimestamp = getDateWithUTC(yd[0]?.timeStamp).startOf("year")

    data.push({
      time: time,
      dataUsage: dataUsage,
      label: label,
      fromTimestamp: fromTimestamp,
    })
  }

  return data
}

const totalDataUsed = (usageData: Array<UsageMetrics>): number => {
  return usageData.reduce((sum, x) => sum + (x?.mbCount ?? 0), 0)
}

export const getTotalDataUsed = (usageData: Array<UsageMetrics>): string => {
  return mbGbDisplay(totalDataUsed(usageData))
}

export const getDataAveragePerSession = (usageData: Array<UsageMetrics>): string => {
  return mbGbDisplay(totalDataUsed(usageData) / usageData.length)
}

export const getDateOfHigestUsage = (usageData: Array<UsageMetrics>, resolution?: RESOLUTIONS) => {
  if (usageData.length < 1) {
    return "-"
  } else {
    const objectWithHighestDataUse = usageData.reduce((prev, current) => {
      return (prev.mbCount ?? 0) > (current.mbCount ?? 0) ? prev : current
    })

    const date = getDateWithUTC(objectWithHighestDataUse.timeStamp)

    if (resolution === RESOLUTIONS.year) {
      return date.format("MMM YYYY")
    } else {
      return date.format("DD MMM YYYY")
    }
  }
}

export const getInitialTimeStamps = () => {
  const now = dayjs().startOf("year").startOf("month").startOf("day")
  const fromTimestamp = now.format("YYYY-MM-DDTHH:mm:ssZ")
  const toTimestamp = now.add(1, "year").format("YYYY-MM-DDTHH:mm:ssZ")

  return { now, fromTimestamp, toTimestamp }
}
